import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { User } from "firebase/auth";

import { appCheckFetch, firebaseApp } from "@/lib/firebase";
import { trackEvent } from "@/lib/analytics";

export type UserRole = "admin" | "user";

interface AuthContextValue {
  user: User | null;
  role: UserRole | null;
  isAdmin: boolean;
  isLoading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  registerWithEmail: (name: string, email: string, password: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  resendVerification: () => Promise<void>;
  refreshRole: () => Promise<void>;
  logout: () => Promise<void>;
}

interface RoleResponse {
  role: UserRole;
  isAdmin: boolean;
  claimsUpdated: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

async function syncServerRole(user: User): Promise<UserRole> {
  const idToken = await user.getIdToken();
  const response = await appCheckFetch("/api/auth-role", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("No fue posible verificar los permisos de la cuenta.");
  }

  const data = (await response.json()) as RoleResponse;

  if (data.claimsUpdated) {
    await user.getIdToken(true);
  }

  return data.role;
}

async function getRoleFromToken(user: User): Promise<UserRole> {
  const token = await user.getIdTokenResult();
  return token.claims.admin === true ? "admin" : "user";
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<UserRole | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const resolveRole = useCallback(async (currentUser: User) => {
    try {
      const syncedRole = await syncServerRole(currentUser);
      setRole(syncedRole);
    } catch {
      try {
        setRole(await getRoleFromToken(currentUser));
      } catch {
        setRole("user");
      }
    }
  }, []);

  useEffect(() => {
    let isActive = true;
    let unsubscribe = () => {};
    const loadingFallback = window.setTimeout(() => {
      if (isActive) setIsLoading(false);
    }, 3_000);

    void import("firebase/auth")
      .then(({ getAuth, onAuthStateChanged }) => {
        if (!isActive) return;

        const auth = getAuth(firebaseApp);
        unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
          setUser(currentUser);

          if (currentUser) {
            await resolveRole(currentUser);
          } else {
            setRole(null);
          }

          if (isActive) {
            window.clearTimeout(loadingFallback);
            setIsLoading(false);
          }
        });
      })
      .catch(() => {
        if (isActive) setIsLoading(false);
      });

    return () => {
      isActive = false;
      window.clearTimeout(loadingFallback);
      unsubscribe();
    };
  }, [resolveRole]);

  const signInWithGoogle = useCallback(async () => {
    const { getAuth, GoogleAuthProvider, signInWithPopup } = await import("firebase/auth");
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    await signInWithPopup(getAuth(firebaseApp), provider);
    void trackEvent("login", { method: "google" });
  }, []);

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    const { getAuth, signInWithEmailAndPassword } = await import("firebase/auth");
    await signInWithEmailAndPassword(getAuth(firebaseApp), email.trim(), password);
    void trackEvent("login", { method: "email" });
  }, []);

  const registerWithEmail = useCallback(async (name: string, email: string, password: string) => {
    const { createUserWithEmailAndPassword, getAuth, sendEmailVerification, updateProfile } =
      await import("firebase/auth");
    const credential = await createUserWithEmailAndPassword(
      getAuth(firebaseApp),
      email.trim(),
      password,
    );

    await updateProfile(credential.user, { displayName: name.trim() });
    await sendEmailVerification(credential.user);
    await credential.user.reload();
    setUser(getAuth(firebaseApp).currentUser);
    void trackEvent("sign_up", { method: "email" });
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    const { getAuth, sendPasswordResetEmail } = await import("firebase/auth");
    await sendPasswordResetEmail(getAuth(firebaseApp), email.trim());
  }, []);

  const resendVerification = useCallback(async () => {
    if (!user) throw new Error("No hay una sesión activa.");
    const { sendEmailVerification } = await import("firebase/auth");
    await sendEmailVerification(user);
  }, [user]);

  const refreshRole = useCallback(async () => {
    if (!user) return;
    await user.reload();
    const { getAuth } = await import("firebase/auth");
    const refreshedUser = getAuth(firebaseApp).currentUser;
    setUser(refreshedUser);
    if (refreshedUser) {
      await refreshedUser.getIdToken(true);
      await resolveRole(refreshedUser);
    }
  }, [resolveRole, user]);

  const logout = useCallback(async () => {
    const { getAuth, signOut } = await import("firebase/auth");
    await signOut(getAuth(firebaseApp));
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      role,
      isAdmin: role === "admin",
      isLoading,
      signInWithGoogle,
      signInWithEmail,
      registerWithEmail,
      resetPassword,
      resendVerification,
      refreshRole,
      logout,
    }),
    [
      isLoading,
      logout,
      refreshRole,
      registerWithEmail,
      resendVerification,
      resetPassword,
      role,
      signInWithEmail,
      signInWithGoogle,
      user,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// El hook y el proveedor comparten el mismo contexto; mantenerlos juntos evita
// que otros módulos puedan consumir una instancia distinta por accidente.
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth debe usarse dentro de AuthProvider.");
  }

  return context;
}
